import { matchReportTechToCatalog, normalizeTechNameForMatch } from './voFlexTechCatalogMatch';

const FLEX_FUELS = ['bio', 'coal', 'gas', 'oil'];

/**
 * Group capacity / sim-gen under flex fuels.
 * Catalog is an overlay (label, parent_fuel, sort). Unlisted flex techs still nest
 * and dispatch using tech + tech_by_fuel identity.
 */

/** Legacy keyword classifier — bio/coal/oil/gas from name + tech_by_fuel. */
export function classifyFlexFuel(tech, techByFuel) {
  const fuel = String(techByFuel || '').toLowerCase();
  const name = String(tech || '').toLowerCase();
  const hay = `${fuel} ${name}`;
  if (hay.includes('bio')) return 'bio';
  if (hay.includes('coal')) return 'coal';
  if (hay.includes('oil')) return 'oil';
  if (hay.includes('gas') || hay.includes('ccgt') || hay.includes('ocgt')) return 'gas';
  return null;
}

export function adhocFlexTechId(tech, techByFuel) {
  const t = normalizeTechNameForMatch(tech);
  const f = normalizeTechNameForMatch(techByFuel) || '-';
  return `adhoc:${t}|${f}`;
}

/**
 * @param {string} tech
 * @param {string|null|undefined} techByFuel
 * @param {import('./voFlexTechCatalogMatch').VoFlexCatalogRow[]|null|undefined} catalog
 * @param {string|null|undefined} engineFuel
 * @returns {{ techId: string, fuel: string, label: string, sortOrder: number, fromCatalog: boolean }|null}
 */
export function resolveFlexTechIdentity(tech, techByFuel, catalog, engineFuel) {
  const name = String(tech || '').trim();
  if (!name) return null;
  const hit = matchReportTechToCatalog(name, techByFuel, catalog);
  if (hit) {
    return {
      techId: hit.techId,
      fuel: hit.parentFuel,
      label: hit.displayName,
      sortOrder: hit.sortOrder ?? 100,
      fromCatalog: true,
    };
  }
  const classified = classifyFlexFuel(name, techByFuel);
  const fuel = classified || (FLEX_FUELS.includes(engineFuel) ? engineFuel : null);
  if (!FLEX_FUELS.includes(fuel)) return null;
  return {
    techId: adhocFlexTechId(name, techByFuel),
    fuel,
    label: name,
    sortOrder: 100,
    fromCatalog: false,
  };
}

function sortFlexKids(list) {
  list.sort(
    (a, b) =>
      (a.sortOrder - b.sortOrder)
      || String(a.label || a.tech || '').localeCompare(String(b.label || b.tech || ''))
      || (b.capacityGw - a.capacityGw)
  );
}

/**
 * @param {Array<{ tech?: string, techByFuel?: string|null, gw?: number }>|null|undefined} capacityByTech
 * @param {import('./voFlexTechCatalogMatch').VoFlexCatalogRow[]|null|undefined} catalog
 */
export function groupCapacityByFlexFuel(capacityByTech, catalog) {
  /** @type {Record<string, Array<{ techId: string, tech: string, techByFuel: string|null, label: string, capacityGw: number, sortOrder: number, fromCatalog: boolean }>>} */
  const out = { bio: [], coal: [], gas: [], oil: [] };
  if (!Array.isArray(capacityByTech)) {
    return out;
  }

  /** @type {Map<string, { techId: string, parentFuel: string, tech: string, techByFuel: string|null, label: string, capacityGw: number, sortOrder: number, fromCatalog: boolean }>} */
  const byId = new Map();

  for (const row of capacityByTech) {
    const tech = String(row?.tech || '').trim();
    if (!tech) continue;
    const techByFuel = row?.techByFuel ?? row?.tech_by_fuel ?? null;
    const capacityGw = Number(row?.gw ?? row?.capacityGw);
    if (!Number.isFinite(capacityGw) || capacityGw <= 0) continue;

    const id = resolveFlexTechIdentity(tech, techByFuel, catalog, null);
    if (!id) continue;

    const prev = byId.get(id.techId);
    if (prev) {
      prev.capacityGw += capacityGw;
    } else {
      byId.set(id.techId, {
        techId: id.techId,
        parentFuel: id.fuel,
        tech,
        techByFuel,
        label: id.label,
        capacityGw,
        sortOrder: id.sortOrder,
        fromCatalog: id.fromCatalog,
      });
    }
  }

  for (const item of byId.values()) {
    if (!out[item.parentFuel]) continue;
    out[item.parentFuel].push(item);
  }
  for (const fuel of Object.keys(out)) {
    sortFlexKids(out[fuel]);
  }
  return out;
}

/**
 * @param {Array<object>|null|undefined} flexDispatchOrder
 * @param {import('./voFlexTechCatalogMatch').VoFlexCatalogRow[]|null|undefined} catalog
 */
export function groupSimGenByFlexFuel(flexDispatchOrder, catalog) {
  /** @type {Record<string, Array<object>>} */
  const out = { bio: [], coal: [], gas: [], oil: [] };
  if (!Array.isArray(flexDispatchOrder)) {
    return out;
  }

  /** @type {Map<string, object>} */
  const byId = new Map();

  for (const row of flexDispatchOrder) {
    const tech = String(row?.tech || '').trim();
    if (!tech) continue;
    const techByFuel = row?.techByFuel ?? null;
    const capacityGw = Number(row?.capacityGw);
    if (!Number.isFinite(capacityGw) || capacityGw <= 0) continue;

    const id = resolveFlexTechIdentity(tech, techByFuel, catalog, row.fuel);
    if (!id) continue;

    const gen = Number.isFinite(row.generationTWh) ? row.generationTWh : 0;
    const util = Number.isFinite(row.utilization) ? row.utilization : 0;
    const prev = byId.get(id.techId);
    if (prev) {
      prev.capacityGw += capacityGw;
      prev.generationTWh += gen;
      prev.utilization =
        prev.capacityGw > 0
          ? (prev.generationTWh * 1_000_000) / (prev.capacityGw * 1000 * 8760)
          : 0;
    } else {
      byId.set(id.techId, {
        techId: id.techId,
        tech,
        techByFuel,
        label: row.label || id.label,
        fuel: id.fuel,
        capacityGw,
        generationTWh: gen,
        utilization: util,
        lastResort: Boolean(row.lastResort),
        sortOrder: id.sortOrder,
        fromCatalog: id.fromCatalog,
      });
    }
  }

  for (const item of byId.values()) {
    if (!out[item.fuel]) continue;
    out[item.fuel].push(item);
  }
  for (const fuel of Object.keys(out)) {
    sortFlexKids(out[fuel]);
  }
  return out;
}

/**
 * Apply techId → GW overrides onto a flex dispatch stack.
 * Multiple stack rows sharing a techId are scaled so their sum equals the override.
 * @param {Array<object>|null|undefined} stack
 * @param {Record<string, number>|null|undefined} appliedByTechId
 */
export function applyVoSubtechCapacityOverrides(stack, appliedByTechId) {
  if (!Array.isArray(stack)) return stack;
  if (!appliedByTechId || typeof appliedByTechId !== 'object') return stack;

  const result = stack.map((row) => ({ ...row }));
  const techIds = new Set(
    result.map((r) => r?.techId).filter((id) => id != null && id !== '')
  );

  for (const techId of techIds) {
    if (!Object.prototype.hasOwnProperty.call(appliedByTechId, techId)) continue;
    const target = Number(appliedByTechId[techId]);
    if (!Number.isFinite(target)) continue;

    const idxs = [];
    for (let i = 0; i < result.length; i++) {
      if (result[i].techId === techId) idxs.push(i);
    }
    if (!idxs.length) continue;

    const sum = idxs.reduce((s, i) => s + (Number(result[i].capacityGw) || 0), 0);
    if (idxs.length === 1 || sum <= 0) {
      result[idxs[0]].capacityGw = Math.max(0, target);
      for (let j = 1; j < idxs.length; j++) result[idxs[j]].capacityGw = 0;
    } else {
      const factor = target / sum;
      for (const i of idxs) {
        result[i].capacityGw = Math.max(0, (Number(result[i].capacityGw) || 0) * factor);
      }
    }
  }

  return result.filter((row) => (Number(row.capacityGw) || 0) > 0);
}

/**
 * Attach catalog overlay; keep every classifiable flex tech (not an allow-list).
 * @param {Array<object>|null|undefined} flexDispatchOrder
 * @param {import('./voFlexTechCatalogMatch').VoFlexCatalogRow[]|null|undefined} catalog
 */
export function enrichFlexDispatchStack(flexDispatchOrder, catalog) {
  const matched = [];
  const unmapped = [];
  if (!Array.isArray(flexDispatchOrder)) {
    return { matched, unmapped };
  }

  for (const row of flexDispatchOrder) {
    const id = resolveFlexTechIdentity(row?.tech, row?.techByFuel, catalog, row?.fuel);
    if (!id) {
      unmapped.push({
        tech: row?.tech,
        techByFuel: row?.techByFuel ?? null,
        fuel: row?.fuel ?? null,
        capacityGw: row?.capacityGw,
        productionTwh: row?.productionTwh,
        reason: 'unclassified_flex',
      });
      continue;
    }
    matched.push({
      ...row,
      techId: id.techId,
      fuel: id.fuel,
      catalogLabel: id.label,
      sortOrder: id.sortOrder,
      fromCatalog: id.fromCatalog,
    });
  }

  matched.sort((a, b) => {
    const ca = Number.isFinite(a.costPerMwh) ? a.costPerMwh : Infinity;
    const cb = Number.isFinite(b.costPerMwh) ? b.costPerMwh : Infinity;
    if (ca !== cb) return ca - cb;
    const so = (a.sortOrder ?? 100) - (b.sortOrder ?? 100);
    if (so !== 0) return so;
    return String(a.catalogLabel || a.tech || '').localeCompare(
      String(b.catalogLabel || b.tech || '')
    );
  });

  return { matched, unmapped };
}

/** @deprecated Use enrichFlexDispatchStack — kept for existing imports. */
export function filterFlexDispatchToCatalog(flexDispatchOrder, catalog) {
  return enrichFlexDispatchStack(flexDispatchOrder, catalog);
}

function isObviousNonFlex(tech, techByFuel) {
  const hay = `${techByFuel || ''} ${tech || ''}`.toLowerCase();
  return /solar|wind|hydro|nuclear|geo|batt|storage|pump|demand/.test(hay);
}

/**
 * Capacity leftovers that are not flex and not an obvious non-flex tech.
 */
export function findUnmappedFlexCapacity(capacityByTech, catalog, classifyFn = classifyFlexFuel) {
  const out = [];
  if (!Array.isArray(capacityByTech)) return out;
  for (const row of capacityByTech) {
    const tech = String(row?.tech || '').trim();
    if (!tech) continue;
    const techByFuel = row?.techByFuel ?? row?.tech_by_fuel ?? null;
    const gw = Number(row?.gw ?? row?.capacityGw);
    if (!Number.isFinite(gw) || gw <= 0) continue;
    if (resolveFlexTechIdentity(tech, techByFuel, catalog, null)) continue;
    if (classifyFn?.(tech, techByFuel)) continue;
    if (isObviousNonFlex(tech, techByFuel)) continue;
    out.push({
      tech,
      techByFuel,
      fuel: null,
      capacityGw: gw,
      reason: 'unclassified_flex',
    });
  }
  return out;
}

/**
 * VO load-time banner: drop names that land in a flex bucket (catalog or classifier)
 * so we only warn about true leftovers.
 *
 * @param {Array<{ name: string, gw: number }>|null|undefined} unmapped
 * @param {Array<{ tech?: string, techByFuel?: string|null, tech_by_fuel?: string|null, gw?: number }>|null|undefined} capacityByTech
 * @param {import('./voFlexTechCatalogMatch').VoFlexCatalogRow[]|null|undefined} catalog
 * @returns {Array<{ name: string, gw: number }>}
 */
export function filterUnmappedTechsCoveredByFlexCatalog(unmapped, capacityByTech, catalog) {
  if (!Array.isArray(unmapped) || unmapped.length === 0) return [];
  if (!Array.isArray(capacityByTech)) {
    return unmapped;
  }

  return unmapped
    .map((entry) => {
      const name = String(entry?.name ?? '').trim();
      if (!name) return entry;
      const nameNorm = normalizeTechNameForMatch(name);

      const rows = capacityByTech.filter((row) => {
        const tech = String(row?.tech || '').trim();
        if (!tech) return false;
        return tech === name || normalizeTechNameForMatch(tech) === nameNorm;
      });

      if (!rows.length) return entry;

      let matchedGw = 0;
      let unmatchedGw = 0;
      for (const row of rows) {
        const gw = Number(row?.gw ?? row?.capacityGw);
        if (!Number.isFinite(gw) || gw <= 0) continue;
        const techByFuel = row?.techByFuel ?? row?.tech_by_fuel ?? null;
        if (resolveFlexTechIdentity(row.tech, techByFuel, catalog, null)) {
          matchedGw += gw;
        } else {
          unmatchedGw += gw;
        }
      }

      if (matchedGw > 0 && unmatchedGw <= 0) return null;
      if (matchedGw > 0 && unmatchedGw > 0) {
        return {
          name: entry.name,
          gw: Math.round(unmatchedGw * 1e6) / 1e6,
        };
      }
      return entry;
    })
    .filter(Boolean);
}
